import { FormEvent, useCallback, useEffect, useState } from "react";
import {
    fetchNominationBlocklist,
    unblockNominationBlocklistEntries,
    updateNominationBlocklistEntry,
    type NominationBlocklistEntry,
} from "../api/tcg";

type AdminBlocklistProps = {
    token: string;
    categoryId: number;
    reloadKey: number;
    onChanged?: () => Promise<void> | void;
};

function formatCreatedAt(value: string): string {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString("de-DE");
}

function AdminBlocklist({ token, categoryId, reloadKey, onChanged }: AdminBlocklistProps) {
    const [entries, setEntries] = useState<NominationBlocklistEntry[]>([]);
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [searchInput, setSearchInput] = useState("");
    const [search, setSearch] = useState("");
    const [includeKnownAliases, setIncludeKnownAliases] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editDisplayText, setEditDisplayText] = useState("");
    const [editReason, setEditReason] = useState("");
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const loadBlocklist = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const nextEntries = await fetchNominationBlocklist(token, categoryId, search);
            setEntries(nextEntries);
            const availableIds = new Set(nextEntries.map((entry) => entry.id));
            setSelectedIds((current) => current.filter((id) => availableIds.has(id)));
            setEditingId((current) =>
                current !== null && availableIds.has(current) ? current : null
            );
        } catch (err) {
            setError(err instanceof Error ? err.message : "Blockierte Nominierungen konnten nicht geladen werden.");
        } finally {
            setLoading(false);
        }
    }, [token, categoryId, search]);

    useEffect(() => {
        void loadBlocklist();
    }, [loadBlocklist, reloadKey]);

    function handleSearch(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();
        setMessage(null);
        setError(null);
        setSelectedIds([]);
        cancelEditing();
        const nextSearch = searchInput.trim();
        if (nextSearch === search) {
            void loadBlocklist();
        } else {
            setSearch(nextSearch);
        }
    }

    function resetSearch() {
        setSearchInput("");
        setSelectedIds([]);
        setMessage(null);
        setError(null);
        cancelEditing();

        if (search === "") {
            void loadBlocklist();
        } else {
            setSearch("");
        }
    }

    function toggleSelected(id: number) {
        setMessage(null);
        setError(null);
        setSelectedIds((current) =>
            current.includes(id)
                ? current.filter((selectedId) => selectedId !== id)
                : [...current, id]
        );
    }

    function startEditing(entry: NominationBlocklistEntry) {
        setMessage(null);
        setError(null);
        setEditingId(entry.id);
        setEditDisplayText(entry.display_text);
        setEditReason(entry.reason ?? "");
    }

    function cancelEditing() {
        setEditingId(null);
        setEditDisplayText("");
        setEditReason("");
    }

    async function handleSave(entryId: number) {
        const displayText = editDisplayText.trim();
        if (!displayText) {
            setError("Der Anzeigename darf nicht leer sein.");
            return;
        }

        setBusy(true);
        setError(null);
        setMessage(null);
        try {
            const updated = await updateNominationBlocklistEntry(token, entryId, {
                displayText,
                reason: editReason.trim() || null,
            });
            setEntries((current) =>
                current.map((entry) => entry.id === updated.id ? updated : entry)
            );
            cancelEditing();
            await loadBlocklist();
            setMessage("Blocklist-Eintrag wurde aktualisiert.");
        } catch (err) {
            setError(err instanceof Error ? err.message : "Blocklist-Eintrag konnte nicht aktualisiert werden.");
        } finally {
            setBusy(false);
        }
    }

    async function handleUnblock(ids: number[]) {
        if (ids.length === 0) return;

        const confirmation = ids.length === 1
            ? "Möchtest du diesen Eintrag wirklich aus der Blocklist entfernen? Danach kann dieser Begriff wieder nominiert werden. Bereits gelöschte Nominierungen werden nicht wiederhergestellt."
            : "Möchtest du die ausgewählten Einträge wirklich entsperren? Danach können diese Begriffe wieder nominiert werden. Bereits gelöschte Nominierungen werden nicht wiederhergestellt.";
        if (!window.confirm(confirmation)) return;

        setBusy(true);
        setError(null);
        setMessage(null);
        try {
            await unblockNominationBlocklistEntries(
                token,
                categoryId,
                ids,
                includeKnownAliases
            );
            setSelectedIds([]);
            if (editingId !== null && ids.includes(editingId)) cancelEditing();
            await loadBlocklist();
            await onChanged?.();
            setMessage(
                ids.length === 1
                    ? "Blocklist-Eintrag wurde entsperrt."
                    : "Ausgewählte Blocklist-Einträge wurden entsperrt."
            );
        } catch (err) {
            setError(err instanceof Error ? err.message : "Blocklist-Einträge konnten nicht entsperrt werden.");
        } finally {
            setBusy(false);
        }
    }

    const hasActiveSearch = search.length > 0;

    return (
        <div className="admin-action-block admin-blocklist">
            <h3 className="admin-subtitle">Blockierte Nominierungen verwalten</h3>
            <p className="admin-note admin-blocklist-description">
                Hier können blockierte Nominierungseinträge eingesehen, bearbeitet oder wieder
                freigegeben werden. Das ist hilfreich, falls ein Eintrag versehentlich gelöscht
                und blockiert wurde.
            </p>
            <p className="admin-note admin-blocklist-warning">
                Beim Entsperren wird nur die Sperre entfernt. Bereits gelöschte Nominierungen oder
                Stimmen werden nicht wiederhergestellt.
            </p>

            <form className="admin-blocklist-search" onSubmit={handleSearch}>
                <label className="admin-label" htmlFor="nomination-blocklist-search">
                    Blocklist durchsuchen
                </label>
                <div className="admin-blocklist-search-row">
                    <input
                        id="nomination-blocklist-search"
                        className="admin-input"
                        type="search"
                        value={searchInput}
                        onChange={(event) => setSearchInput(event.target.value)}
                        placeholder="Nach Anzeigename oder normalisiertem Wert suchen …"
                        disabled={loading || busy}
                    />
                    <button className="admin-btn-secondary" type="submit" disabled={loading || busy}>
                        Suchen
                    </button>
                </div>
            </form>

            {message && <p className="admin-success">{message}</p>}
            {error && <p className="admin-error">{error}</p>}

            {loading ? (
                <p className="admin-note">Blockierte Nominierungen werden geladen …</p>
            ) : entries.length === 0 && !error ? (
                <div className="admin-blocklist-empty">
                    <p className="admin-note">
                        {hasActiveSearch
                            ? "Keine blockierten Einträge für diese Suche gefunden."
                            : "Für diese Kategorie sind aktuell keine Nominierungen blockiert."}
                    </p>
                    {hasActiveSearch && (
                        <button
                            className="admin-btn-secondary"
                            type="button"
                            onClick={resetSearch}
                            disabled={busy}
                        >
                            Suche zurücksetzen
                        </button>
                    )}
                </div>
            ) : entries.length > 0 ? (
                <>
                    <div className="admin-blocklist-toolbar">
                        <label className="admin-blocklist-alias-option">
                            <input
                                type="checkbox"
                                checked={includeKnownAliases}
                                onChange={(event) => setIncludeKnownAliases(event.target.checked)}
                                disabled={busy}
                            />
                            <span>
                                <strong>Bekannte Aliasvarianten ebenfalls entsperren</strong>
                                <small>
                                    Wenn ein Begriff vorher aus zusammengeführten Varianten
                                    entstanden ist, werden bekannte Schreibvarianten ebenfalls
                                    entsperrt. Wenn du nur die ausgewählten Einträge entsperren
                                    möchtest, lasse diese Option deaktiviert.
                                </small>
                            </span>
                        </label>
                        <button
                            className="admin-btn-primary"
                            type="button"
                            onClick={() => void handleUnblock(selectedIds)}
                            disabled={busy || selectedIds.length === 0}
                        >
                            Entsperren
                        </button>
                    </div>

                    <div className="admin-table-scroll">
                        <table className="admin-table admin-blocklist-table">
                            <thead>
                                <tr>
                                    <th>Auswahl</th>
                                    <th>Anzeigename</th>
                                    <th>Normalisierter Wert</th>
                                    <th>Grund</th>
                                    <th>Geltungsbereich</th>
                                    <th>Erstellt</th>
                                    <th>Aktionen</th>
                                </tr>
                            </thead>
                            <tbody>
                                {entries.map((entry) => {
                                    const isEditing = editingId === entry.id;
                                    const scope = entry.scope === "global" || entry.category_id === null
                                        ? "Global"
                                        : "Kategorie";

                                    return (
                                        <tr key={entry.id}>
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedIds.includes(entry.id)}
                                                    onChange={() => toggleSelected(entry.id)}
                                                    disabled={busy}
                                                    aria-label={`${entry.display_text} auswählen`}
                                                />
                                            </td>
                                            <td>
                                                {isEditing ? (
                                                    <input
                                                        className="admin-input admin-blocklist-edit-input"
                                                        value={editDisplayText}
                                                        onChange={(event) => setEditDisplayText(event.target.value)}
                                                        aria-label="Anzeigename"
                                                        disabled={busy}
                                                    />
                                                ) : entry.display_text}
                                            </td>
                                            <td><code>{entry.normalized_text}</code></td>
                                            <td>
                                                {isEditing ? (
                                                    <input
                                                        className="admin-input admin-blocklist-edit-input"
                                                        value={editReason}
                                                        onChange={(event) => setEditReason(event.target.value)}
                                                        aria-label="Grund"
                                                        disabled={busy}
                                                    />
                                                ) : entry.reason || "–"}
                                            </td>
                                            <td>
                                                <span className={`admin-blocklist-scope scope-${entry.scope}`}>
                                                    {scope}
                                                </span>
                                            </td>
                                            <td>{formatCreatedAt(entry.created_at)}</td>
                                            <td>
                                                <div className="admin-blocklist-actions">
                                                    {isEditing ? (
                                                        <>
                                                            <button
                                                                className="admin-btn-primary"
                                                                type="button"
                                                                onClick={() => void handleSave(entry.id)}
                                                                disabled={busy}
                                                            >
                                                                Speichern
                                                            </button>
                                                            <button
                                                                className="admin-btn-secondary"
                                                                type="button"
                                                                onClick={cancelEditing}
                                                                disabled={busy}
                                                            >
                                                                Abbrechen
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <button
                                                                className="admin-btn-secondary"
                                                                type="button"
                                                                onClick={() => startEditing(entry)}
                                                                disabled={busy}
                                                            >
                                                                Bearbeiten
                                                            </button>
                                                            <button
                                                                className="admin-btn-secondary"
                                                                type="button"
                                                                onClick={() => void handleUnblock([entry.id])}
                                                                disabled={busy}
                                                            >
                                                                Entsperren
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </>
            ) : null}
        </div>
    );
}

export default AdminBlocklist;
